import { createAxiosInstance } from "@/services/util";
import Axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import Vue from "vue";
import { useTosti } from "@/plugins/vuetify-tosti";
import { eventbus } from "@/eventbus";
import { ObjectLiteral } from "@/types/misc";

interface ServiceConfig {
    apiPrefix: string;
    entityName: string;
}

export interface FindResult<T> {
    data: T[];
    total?: number;
}

export interface GetResult<T> {
    data: T;
    previousId?: number;
    nextId?: number;
}

export interface BaseEntity extends ObjectLiteral {
    id?: number;
}

export abstract class BaseCrudService<T extends BaseEntity, CreateDTO = Partial<T>, UpdateDTO = Partial<T>> {
    protected tosti = useTosti();
    protected axios: AxiosInstance;
    protected config: ServiceConfig;

    protected constructor(config: ServiceConfig) {
        this.config = config;

        this.axios = createAxiosInstance({
            baseURL: `${process.env.VUE_APP_API_BASE}/${config.apiPrefix}`,
        });
    }

    async create(data: CreateDTO, axiosOptions?: AxiosRequestConfig, successMessage: boolean = true): Promise<T> {
        try {
            const res = await this.axios.post<T>(``, this.cleanup(Object.assign({}, data)), axiosOptions);
            eventbus.$emit(`${this.config.entityName}:create`, res.data);
            if (successMessage) this.tosti.success({ message: `Created ${this.config.entityName}` });
            return res.data;
        } catch (e) {
            this.tosti.error({
                message: `Could not create ${this.config.entityName}: ${e.response?.data?.message || e.message}`,
            });
            throw e;
        }
    }

    async get(id: number, axiosOptions?: AxiosRequestConfig): Promise<T> {
        try {
            const res = await this.axios.get<T>(`/${id}`, axiosOptions);
            return res.data;
        } catch (e) {
            this.tosti.error({
                message: `Could not retrieve ${this.config.entityName} ${id}: ${
                    e.response?.data?.message || e.message
                }`,
            });
            throw e;
        }
    }

    async getNew(id: number, axiosOptions?: AxiosRequestConfig): Promise<GetResult<T>> {
        try {
            const res = await this.axios.get<T>(`/${id}`, axiosOptions);
            return {
                data: res.data,
                nextId: res.headers["x-next-id"] ? parseInt(res.headers["x-next-id"]) : null,
                previousId: res.headers["x-previous-id"] ? parseInt(res.headers["x-previous-id"]) : null,
            };
        } catch (e) {
            this.tosti.error({
                message: `Could not retrieve ${this.config.entityName} ${id}: ${
                    e.response?.data?.message || e.message
                }`,
            });
            throw e;
        }
    }

    /**
     * @deprecated use findNew instead
     */
    async find(axiosOptions?: AxiosRequestConfig): Promise<T[]> {
        try {
            const res = await this.axios.get<T[]>(``, axiosOptions);
            return res.data;
        } catch (e) {
            this.tosti.error({
                message: `Could not retrieve ${this.config.entityName}s: ${e.response?.data?.message || e.message}`,
            });
            throw e;
        }
    }

    async findNew(axiosOptions?: AxiosRequestConfig): Promise<FindResult<T>> {
        try {
            const res = await this.axios.get<T[]>(``, axiosOptions);
            return {
                data: res.data,
                total: parseInt(res.headers["x-total"]),
            };
        } catch (e) {
            this.tosti.error({
                message: `Could not retrieve ${this.config.entityName}s: ${e.response?.data?.message || e.message}`,
            });
            throw e;
        }
    }

    async patch(
        id: number,
        fields: UpdateDTO,
        axiosOptions?: AxiosRequestConfig,
        successMessage: boolean = true,
    ): Promise<T> {
        try {
            // Todo: should be patch. Put is for replacing entire documents
            const res = await this.axios.put<T>(`/${id}`, this.cleanup(Object.assign({}, fields)), axiosOptions);
            eventbus.$emit(`${this.config.entityName}:update`, res.data);
            if (successMessage) this.tosti.success({ message: `Updated ${this.config.entityName}` });
            return res.data;
        } catch (e) {
            this.tosti.error({
                message: `Could not patch ${this.config.entityName} ${id}: ${e.response?.data?.message || e.message}`,
            });
            throw e;
        }
    }

    async delete(id: number, axiosOptions?: AxiosRequestConfig, successMessage: boolean = true): Promise<void> {
        try {
            await this.axios.delete(`/${id}`, axiosOptions);
            eventbus.$emit(`${this.config.entityName}:delete`, id);
            if (successMessage) this.tosti.success({ message: `Deleted ${this.config.entityName}` });
        } catch (e) {
            this.tosti.error({
                message: `Could not retrieve ${this.config.entityName} ${id}: ${
                    e.response?.data?.message || e.message
                }`,
            });
            throw e;
        }
    }

    cleanup(item: Partial<T>): Partial<T> {
        return item;
    }
}
